import { calculateSteelWeight } from "@/pages/ItemEditor/steelWeight";

import { NOMINAL_UNIT_WEIGHT } from "./constants";
import type { RfqLine, RfqQuote, RfqQuoteMeta } from "./types";

export function fmtMoney(n: number): string {
  return new Intl.NumberFormat("zh-TW", { maximumFractionDigits: 0 }).format(
    Math.round(n),
  );
}

export function fmtUnitPrice(n: number): string {
  return new Intl.NumberFormat("zh-TW", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(n);
}

export function fmtWeight(n: number): string {
  return new Intl.NumberFormat("zh-TW", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export interface RfqCalcLabels {
  manualInput: string;
  manualOverride: string;
  quote: string;
  lineWeight: string;
  freightShare: string;
  compareBase: string;
  noWeight: string;
  taxIncl: string;
  taxExcl: string;
  freightIncluded: string;
  freightApportioned: string;
  exw: string;
  perUnit: (unit: string) => string;
  pieces: string;
  unitMismatch: (quoteUnit: string, lineUnit: string) => string;
  taxUpliftNote: string;
}

export const DEFAULT_CALC_LABELS: RfqCalcLabels = {
  manualInput: "人工輸入",
  manualOverride: "人工覆寫",
  quote: "報價",
  lineWeight: "本項總重",
  freightShare: "運費分攤",
  compareBase: "比價基準",
  noWeight: "無總重，無法換算",
  taxIncl: "含稅",
  taxExcl: "未稅",
  freightIncluded: "含運",
  freightApportioned: "加運費分攤",
  exw: "出廠",
  perUnit: (unit: string) => `元/${unit}`,
  pieces: "支",
  unitMismatch: (quoteUnit: string, lineUnit: string) =>
    `報價單位 ${quoteUnit} 與詢價單位 ${lineUnit} 不同，無法自動換算`,
  taxUpliftNote: "含稅 ×1.05",
};

export interface WeightResult {
  kg: number;
  formula: string;
}

export function estimateWeight(
  line: RfqLine,
  labels: RfqCalcLabels = DEFAULT_CALC_LABELS,
): WeightResult | null {
  const len = line.lengthM ?? 0;
  const count = line.count ?? line.qty ?? 0;
  const spec = line.nominalSpec ?? "";
  const uw =
    line.unitWeight ??
    (line.sectionType && spec
      ? calculateSteelWeight(line.sectionType, spec)?.unitWeightKgM
      : undefined) ??
    NOMINAL_UNIT_WEIGHT[spec];

  if (uw && len && count) {
    const kg = uw * len * count;
    const section = [line.sectionType, spec].filter(Boolean).join(" ");
    const uwText = section ? `${section} (${uw} kg/m)` : `${uw} kg/m`;
    return {
      kg,
      formula: `${uwText} × ${len} m × ${count} ${labels.pieces} = ${fmtWeight(kg)} kg`,
    };
  }

  if (line.totalWeightKg) {
    return { kg: line.totalWeightKg, formula: labels.manualInput };
  }
  return null;
}

export function resolveWeight(
  line: RfqLine,
  labels: RfqCalcLabels = DEFAULT_CALC_LABELS,
): WeightResult | null {
  if (line.weightOverride && line.totalWeightKg != null) {
    return {
      kg: line.totalWeightKg,
      formula: `${labels.manualOverride} ${fmtWeight(line.totalWeightKg)} kg`,
    };
  }
  const est = estimateWeight(line, labels);
  if (est) return est;
  if (line.totalWeightKg != null) {
    return {
      kg: line.totalWeightKg,
      formula: `${labels.manualInput} ${fmtWeight(line.totalWeightKg)} kg`,
    };
  }
  return null;
}

export interface CompareSettings {
  tax: "excl" | "incl";
  freight: "exw" | "dap";
}

export interface CompareResult {
  raw: number;
  rawUnit: string;
  base: number | null;
  baseUnit: string;
  subtotal: number;
  weightKg: number | null;
  formula: string;
  comparable: boolean;
}

export function computeCompare(
  line: RfqLine,
  quote: RfqQuote,
  meta: RfqQuoteMeta | undefined,
  vendorLineSubtotals: number[],
  settings: CompareSettings,
  labels: RfqCalcLabels = DEFAULT_CALC_LABELS,
): CompareResult {
  const rawSubtotal = quote.quoteQty * quote.unitPrice;
  const rawUnit = labels.perUnit(quote.quoteUnit);

  let freightShare = 0;
  if (settings.freight === "dap" && !quote.freightIncluded && meta) {
    const vendorTotal = vendorLineSubtotals.reduce((a, b) => a + b, 0);
    if (vendorTotal > 0) {
      freightShare = meta.freightTotal * (rawSubtotal / vendorTotal);
    }
  }

  let subtotal = rawSubtotal + freightShare;
  if (settings.tax === "incl" && !quote.taxIncluded) {
    subtotal = subtotal * 1.05;
  }

  const taxNote = settings.tax === "incl" ? labels.taxIncl : labels.taxExcl;
  const freightNote =
    settings.freight === "dap"
      ? quote.freightIncluded
        ? labels.freightIncluded
        : labels.freightApportioned
      : labels.exw;

  const steps: string[] = [];
  steps.push(
    `${labels.quote} ${fmtMoney(quote.unitPrice)} ${labels.perUnit(quote.quoteUnit)} × ${quote.quoteQty} ${quote.quoteUnit} = ${fmtMoney(rawSubtotal)}`,
  );

  if (line.priceMode === "weight") {
    const w = resolveWeight(line, labels);
    if (!w || w.kg <= 0) {
      return {
        raw: quote.unitPrice,
        rawUnit,
        base: null,
        baseUnit: "元/kg",
        subtotal,
        weightKg: null,
        formula: labels.noWeight,
        comparable: false,
      };
    }
    steps.push(`${labels.lineWeight} ${w.formula}`);
    if (freightShare > 0) {
      steps.push(
        `${labels.freightShare} + ${fmtMoney(freightShare)} → ${fmtMoney(subtotal)}`,
      );
    }
    if (settings.tax === "incl" && !quote.taxIncluded) {
      steps.push(`${labels.taxUpliftNote} → ${fmtMoney(subtotal)}`);
    }
    const base = subtotal / w.kg;
    steps.push(
      `${labels.compareBase} ${fmtMoney(subtotal)} ÷ ${fmtWeight(w.kg)} = ${fmtUnitPrice(base)} 元/kg (${taxNote}・${freightNote})`,
    );
    return {
      raw: quote.unitPrice,
      rawUnit,
      base,
      baseUnit: "元/kg",
      subtotal,
      weightKg: w.kg,
      formula: steps.join("\n"),
      comparable: true,
    };
  }

  const base = quote.quoteQty > 0 ? subtotal / quote.quoteQty : null;
  const comparable = quote.quoteUnit === line.unit;
  if (freightShare > 0) {
    steps.push(
      `${labels.freightShare} + ${fmtMoney(freightShare)} → ${fmtMoney(subtotal)}`,
    );
  }
  if (settings.tax === "incl" && !quote.taxIncluded) {
    steps.push(`${labels.taxUpliftNote} → ${fmtMoney(subtotal)}`);
  }
  if (!comparable) {
    steps.push(`⚠️ ${labels.unitMismatch(quote.quoteUnit, line.unit)}`);
  } else if (base != null) {
    steps.push(
      `${labels.compareBase} ${fmtMoney(subtotal)} ÷ ${quote.quoteQty} = ${fmtUnitPrice(base)} ${labels.perUnit(line.unit)}`,
    );
  }
  return {
    raw: quote.unitPrice,
    rawUnit,
    base: comparable ? base : null,
    baseUnit: labels.perUnit(line.unit),
    subtotal,
    weightKg: null,
    formula: steps.join("\n"),
    comparable,
  };
}
